import { SuretyQuoteStatus } from "@inrev/common";
import { cn } from "@portal/components/lib/utils";
import { Button, Icon } from "@portal/components/primitives";
import { getStatusCardByStatus } from "@portal/components/ui/StatusCard";
import { PortaledTooltip } from "@portal/components/ui/Tooltip";
import { useOnClickOutside } from "@portal/hooks/useOnClickOutside";
import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { bondFormUnavailableMessage } from "./bondFormAvailability";

export type QuoteStatusUpdate = Extract<
	SuretyQuoteStatus,
	"review" | "approved" | "declined" | "closed" | "accepted" | "pending"
>;

type StatusTransitionOption = {
	value: QuoteStatusUpdate;
	label: string;
};

type MenuPosition = { top: number; left: number; width: number };

const MENU_OFFSET = 4;

/* The menu itself is portaled at 9999 so the tooltip has to clear it. */
const TOOLTIP_Z_INDEX = 10000;

type AdminBondRequestStatusTransitionDropdownProps = {
	currentStatus: SuretyQuoteStatus;
	onStatusChange: (newStatus: QuoteStatusUpdate) => void;
	isLoading?: boolean;
	className?: string;
	isComplete: boolean;
	canApprove: boolean;
	bondFormAvailable?: boolean;
	bondFormIsFetching?: boolean;
	bondFormError?: Error | null;
	trackingPrefix?: string;
};

// Define allowed status transitions based on requirements
const getStatusTransitionOptions = (
	currentStatus: SuretyQuoteStatus,
	canApprove: boolean,
	isComplete: boolean,
): StatusTransitionOption[] => {
	const allStatuses: StatusTransitionOption[] = [
		{ value: "review", label: "In Review" },
		{ value: "approved", label: "Approved" },
		{ value: "declined", label: "Declined" },
		{ value: "closed", label: "Closed" },
		{ value: "accepted", label: "Accepted" },
		{ value: "pending", label: "Pending" },
	];

	const allowedTransitions: Record<SuretyQuoteStatus, QuoteStatusUpdate[]> = {
		review: ["approved", "declined", "closed"],
		approved: ["review", "declined", "closed"],
		declined: ["review", "approved", "closed"],
		accepted: ["review", "declined", "closed"],
		closed: ["review", "approved", "declined"],
		pending: ["closed"],
		bound: [],
	};

	const allowedStatuses = allowedTransitions[currentStatus] || [];

	// Filter to only show allowed transitions (exclude current status)
	let filteredStatuses = allStatuses.filter((status) => allowedStatuses.includes(status.value));

	// Remove "Approved" option if canApprove or isComplete are false
	if (!canApprove || !isComplete) {
		filteredStatuses = filteredStatuses.filter((status) => status.value !== "approved");
	}

	return filteredStatuses;
};

export const AdminBondRequestStatusTransitionDropdown = ({
	currentStatus,
	onStatusChange,
	isLoading = false,
	className,
	isComplete,
	canApprove,
	bondFormAvailable = true,
	bondFormIsFetching = false,
	bondFormError = null,
	trackingPrefix,
}: AdminBondRequestStatusTransitionDropdownProps) => {
	const [isOpen, setIsOpen] = useState(false);
	const triggerRef = useRef<HTMLDivElement>(null);
	const menuRef = useRef<HTMLDivElement>(null);
	const [menuPosition, setMenuPosition] = useState<MenuPosition | null>(null);
	const tooltipId = useId();
	const blockedApprovedMessage = bondFormUnavailableMessage(
		bondFormIsFetching,
		bondFormError,
		"approving",
	);

	useOnClickOutside(menuRef, (event) => {
		if (event.target instanceof Node && triggerRef.current?.contains(event.target)) return;
		setIsOpen(false);
	});

	/* The header this sits in is a collapsible `overflow-hidden` grid, so an absolutely positioned
	   menu gets clipped at the header's bottom edge. Portal it to the body and pin it to the
	   trigger's viewport rect instead. */
	useLayoutEffect(() => {
		if (!isOpen) {
			setMenuPosition(null);
			return;
		}
		const trigger = triggerRef.current;
		if (!trigger) return;
		const rect = trigger.getBoundingClientRect();
		setMenuPosition({ top: rect.bottom + MENU_OFFSET, left: rect.left, width: rect.width });
	}, [isOpen]);

	useEffect(() => {
		if (!isOpen) return;
		const dismiss = (event: Event) => {
			if (event.target instanceof Node && menuRef.current?.contains(event.target)) return;
			setIsOpen(false);
		};
		window.addEventListener("scroll", dismiss, true);
		window.addEventListener("wheel", dismiss, true);
		window.addEventListener("resize", dismiss);
		return () => {
			window.removeEventListener("scroll", dismiss, true);
			window.removeEventListener("wheel", dismiss, true);
			window.removeEventListener("resize", dismiss);
		};
	}, [isOpen]);

	const handleStatusChange = (newStatus: QuoteStatusUpdate) => {
		if (newStatus !== currentStatus) {
			onStatusChange(newStatus);
		}
		setIsOpen(false);
	};

	const options = getStatusTransitionOptions(currentStatus, canApprove, isComplete);

	const triggerCardClass = "w-full";

	// Don't show dropdown if no transitions are allowed
	const hasTransitions = options.length > 0;
	if (!hasTransitions) {
		return (
			<div className={cn("w-full", className)}>
				{getStatusCardByStatus(currentStatus, triggerCardClass)}
			</div>
		);
	}

	return (
		<div ref={triggerRef} className={cn("w-full", className)}>
			<Button
				variant="ghost"
				disabled={isLoading}
				onClick={() => setIsOpen(!isOpen)}
				trackingId={trackingPrefix ? `${trackingPrefix}-menu` : undefined}
				className="h-auto w-full p-0 [&>span]:w-full"
			>
				{getStatusCardByStatus(currentStatus, triggerCardClass, {
					trailing: (
						<Icon
							type="chevron-down"
							size="small"
							color="secondary"
							className={cn("shrink-0 transition-transform duration-200", isOpen && "rotate-180")}
						/>
					),
				})}
			</Button>

			{isOpen &&
				menuPosition &&
				createPortal(
					<div
						ref={menuRef}
						data-testid="status-transition-dropdown-menu"
						className="fixed z-[9999] bg-surface-base border border-bc-default rounded-md shadow-lg"
						style={{ top: menuPosition.top, left: menuPosition.left, width: menuPosition.width }}
					>
						<div className="flex flex-col py-[6px]">
							{options.map((option) => {
								const blocked = !bondFormAvailable && option.value === "approved";
								const optionButton = (
									<Button
										variant="ghost"
										disabled={isLoading || blocked}
										onClick={() => handleStatusChange(option.value)}
										trackingId={trackingPrefix ? `${trackingPrefix}-${option.value}` : undefined}
										className={cn(
											"h-auto w-full px-[10px] py-[4px] hover:bg-surface-light [&>span]:w-full",
											blocked && "cursor-not-allowed opacity-50",
										)}
									>
										{getStatusCardByStatus(option.value, "w-full")}
									</Button>
								);

								if (!blocked) return <div key={option.value}>{optionButton}</div>;

								/* `[&_button]:pointer-events-none` lets the pointer reach the wrapper so the
								   tooltip fires even though the disabled button is otherwise inert. */
								return (
									<span
										key={option.value}
										tabIndex={0}
										aria-label={blockedApprovedMessage}
										data-testid={`status-transition-blocked-${option.value}`}
										data-tooltip-id={tooltipId}
										data-tooltip-content={blockedApprovedMessage}
										className="w-full cursor-not-allowed [&_button]:pointer-events-none"
									>
										{optionButton}
									</span>
								);
							})}
						</div>
						{!bondFormAvailable && (
							<PortaledTooltip
								id={tooltipId}
								place="left"
								style={{ maxWidth: 260, zIndex: TOOLTIP_Z_INDEX }}
							/>
						)}
					</div>,
					document.body,
				)}
		</div>
	);
};
